import React from 'react';
import { ResponsiveLine } from '@nivo/line';

interface DashboardLineChartProps {
    data: Array<{
        id: string;
        color?: string;
        data: Array<{
            x: string | number;
            y: number;
        }>;
    }>;
    xAxisLabel?: string;
    yAxisLabel?: string;
    height?: number;
    enablePoints?: boolean;
    enableGridX?: boolean;
    enableGridY?: boolean;
    enableLegend?: boolean;
    yAxisFormat?: (value: number) => string;
    yValueFormat?: (value: number) => string;
    colors?: any; // Color scheme, array, or function for line colors
}

export const DashboardLineChart: React.FC<DashboardLineChartProps> = ({
    data,
    xAxisLabel = 'Month',
    yAxisLabel = 'Value',
    height,
    enablePoints = true,
    enableGridX = true,
    enableGridY = true,
    enableLegend = true,
    yAxisFormat,
    yValueFormat,
    colors = '#D61F84' // Default to SpotSync pink
}) => {
    const defaultMotionConfig = {
        mass: 1,
        tension: 280,
        friction: 60,
        clamp: false,
        precision: 0.01,
        velocity: 0
    };

    const defaultLegends = [
        {
            anchor: 'bottom-right' as const,
            direction: 'column' as const,
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right' as const,
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle' as const,
            symbolBorderColor: 'rgba(0, 0, 0, .5)',
            effects: [
                {
                    on: 'hover' as const,
                    style: {
                        itemBackground: 'rgba(0, 0, 0, .03)',
                        itemOpacity: 1
                    }
                }
            ]
        }
    ];

    const containerStyle = { 
        height: height ? `${height}px` : '300px',
        width: '100%'
    };

    return (
        <div style={containerStyle}>
            <ResponsiveLine
                data={data}
                margin={{ top: 50, right: 50, bottom: 50, left: 100 }}
                colors={colors}
                xScale={{ type: 'point' }}
                yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: false,
                    reverse: false
                }}
                yFormat={yValueFormat || " >-.2f"}
                animate={true}
                motionConfig={defaultMotionConfig}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: xAxisLabel,
                    legendOffset: 36,
                    legendPosition: 'middle'
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: yAxisLabel,
                    legendOffset: -80,
                    legendPosition: 'middle',
                    format: yAxisFormat
                }}
                enableGridX={enableGridX}
                enableGridY={enableGridY}
                pointSize={enablePoints ? 10 : 0}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={enableLegend ? defaultLegends : []}
            />
        </div>
    );
}; 